import React, { useState } from 'lib/react';
import {
  makeStyles, EVKeyboardArrowDown, EVGrid, EVTypography, EVButton,
} from 'lib/components';
import { PropTypes } from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'lib/translation';
import { hasExpandableContent } from 'utils/app.utils';
import Typography from 'commonComponents/typography';

const useStyles = makeStyles((theme) => ({
  arrowBtnDown: {
    alignContent: 'center',
    height: '44px',
    width: '44px',
    color: theme.evPalette.ocean[600],
    transition: 'background-color 0.5s ease, transform 0.5s ease',
    '&.arrowBtnUp': {
      borderRadius: '4px',
      backgroundColor: `${theme.evPalette.ocean[600]} !important`,
      color: theme.evPalette.salt[100],
      transform: 'rotate(-180deg)',
    },
  },
  expandBtn: {
    cursor: 'pointer',
    padding: '0px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingBottom: '1px',
  },
  tableRow: {
    backgroundColor: 'white',
  },
  tableRowContent: {
    boxShadow: `0 0 0 1px ${theme.evPalette.charcoal[400]}`,
    borderRadius: '4px',
    backgroundColor: 'white',
    overflow: 'hidden',
  },
  moreDetailsSection: {
    padding: '8px 16px',
    backgroundColor: '#F6F7FF',
  },
  expandedContentWrapper: {
    padding: '0px 8px',
    overflow: 'hidden',
    maxHeight: 0,
    transition: 'padding 0.5s linear, max-height 0.1s linear',
    backgroundColor: '#F6F7FF',
    '&.expanded': {
      padding: '8px',
      maxHeight: '100%',
    },
  },
  expandedRowContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
  },
  cellField: {
    flex: '1',
    padding: '16px',
  },
}));
const renderField = (content, label, isLoading) => (
  <EVGrid container direction="column" style={{ gap: '4px' }}>
    {label && !isLoading && (
      <EVGrid item>
        <EVTypography
          style={{
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#1B202C',
            lineHeight: '18px',
          }}
        >
          {label}
        </EVTypography>
      </EVGrid>
    )}
    <EVGrid item style={{ fontSize: '14px', lineHeight: '24px' }}>
      {content?.content || content}
    </EVGrid>
  </EVGrid>
);

function ExpandableCardList(props) {
  const {
    dataList, ExpandedRowComponent, rowKey,
    noResultsMessage, expandedRowFlags, onExpandedRowFlagsChange,
    expandedRowData,
    ...rest
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [expandedRowFlagsState, setExpandedRowFlags] = useState({});

  // #genai Use [key] for computed property name so each row's expand state is tracked correctly
  const handleExpandedRowVisibility = (key) => {
    if (onExpandedRowFlagsChange) return onExpandedRowFlagsChange(key);
    return setExpandedRowFlags((prevState) => ({ ...prevState, [key]: !prevState[key] }));
  };
  const expandedRows = expandedRowFlags || expandedRowFlagsState;

  return (
    <EVGrid className={classes.cardList} {...rest}>
      {(dataList?.length > 0) ? dataList.map((rowData) => {
        const isExpanded = expandedRows[rowData[rowKey]] && hasExpandableContent(rowData.expandedRow);
        const {
          productAndAddress,
          reportIdCell,
          claimNumber,
          date,
          delivery,
          subscriptionDetails,
          statusAndActions,
          isLoading,
        } = rowData;

        return (
          <EVGrid
            key={`row-${rowData[rowKey]}`}
            className={classes.tableRow}
          >
            <EVGrid className={classes.tableRowContent}>
              <EVGrid
                container
                style={{
                  width: '100%',
                }}
              >
                <EVGrid
                  item
                  style={{
                    flex: '2',
                    minWidth: '250px',
                    maxWidth: '350px',
                    padding: '16px',
                  }}
                >
                  {renderField(productAndAddress, null, isLoading)}
                </EVGrid>
                <EVGrid item className={classes.cellField}>
                  {renderField(reportIdCell, t('order_submitted__report_number'), isLoading)}
                </EVGrid>
                <EVGrid item className={classes.cellField}>
                  {renderField(claimNumber, t('common__claim_id'), isLoading)}
                </EVGrid>
                <EVGrid item className={classes.cellField}>
                  {renderField(date, t('order_history__date_ordered'), isLoading)}
                </EVGrid>
                <EVGrid item className={classes.cellField}>
                  {renderField(delivery, t('common_delivery'), isLoading)}
                </EVGrid>
                <EVGrid
                  item
                  style={{
                    flex: '1',
                    minWidth: '210px',
                    maxWidth: '250px',
                    padding: '16px',
                  }}
                >
                  {renderField(subscriptionDetails, null, isLoading)}
                </EVGrid>
                <EVGrid item className={classes.cellField}>
                  {renderField(statusAndActions, null, isLoading)}
                </EVGrid>
              </EVGrid>
              {hasExpandableContent(rowData.expandedRow) && ExpandedRowComponent && (
                <EVGrid className={classes.moreDetailsSection}>
                  <EVButton
                    onClick={() => handleExpandedRowVisibility(rowData[rowKey])}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      textTransform: 'none',
                    }}
                  >
                    <Typography variant="subtitle-400" fontColor="ocean-600" style={{ textDecoration: 'underline' }}>
                      {isExpanded ? t('order_history__less_details') : t('order_history__more_details')}
                    </Typography>
                    <EVKeyboardArrowDown
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        color: '#007CAD',
                      }}
                    />
                  </EVButton>
                </EVGrid>
              )}
              {hasExpandableContent(rowData.expandedRow) && ExpandedRowComponent && (
                <EVGrid className={clsx(classes.expandedContentWrapper, isExpanded && 'expanded')}>
                  <EVGrid className={classes.expandedRowContent}>
                    <ExpandedRowComponent expandedRowData={rowData.expandedRow} />
                  </EVGrid>
                </EVGrid>
              )}
            </EVGrid>
          </EVGrid>
        );
      })
        : (
          <EVGrid className="no-results">
            <EVTypography>{noResultsMessage}</EVTypography>
          </EVGrid>
        )}
    </EVGrid>
  );
}

ExpandableCardList.defaultProps = {
  noResultsMessage: 'No Results',
  expandedRowData: null,
  ExpandedRowComponent: null,
  expandedRowFlags: null,
  onExpandedRowFlagsChange: null,
};

ExpandableCardList.propTypes = {
  dataList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  rowKey: PropTypes.string.isRequired,
  noResultsMessage: PropTypes.string,
  expandedRowData: PropTypes.arrayOf(PropTypes.shape()),
  ExpandedRowComponent: PropTypes.func,
  expandedRowFlags: PropTypes.shape({}),
  onExpandedRowFlagsChange: PropTypes.func,
};

export default ExpandableCardList;
